import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Stack, TextField, Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { Commitment } from "../api/types";
import { parseMoney } from "../lib/money";
import { komunikatRejestru, useNipLookup } from "../lib/useNipLookup";

const RODZAJE = [
  { value: "zakup_drobny", label: "Zakup drobny" },
  { value: "po", label: "Zamówienie (PO)" },
  { value: "najem", label: "Najem" },
  { value: "b2b", label: "Umowa B2B" },
];

/**
 * Szybkie utworzenie rodzica dla kosztu.
 *
 * Zasada „każdy koszt ma rodzica" jest wykonalna tylko wtedy, gdy da się go
 * założyć bez wychodzenia z opisywania. Reguła bez wykonalnej ścieżki zamienia
 * się w obchodzenie reguły.
 */
export function NewCommitmentDialog({
  open, onClose, projectCode,
}: { open: boolean; onClose: () => void; projectCode: string }) {
  const [form, setForm] = useState({
    kind: "zakup_drobny", title: "", counterparty_name: "", counterparty_nip: "",
    amount_net: "",
  });
  const [blad, setBlad] = useState<ApiError | null>(null);
  const [zRejestru, setZRejestru] = useState("");
  const kolejka = useQueryClient();
  const rejestr = useNipLookup(form.counterparty_nip);

  // Nazwa z rejestru wchodzi tylko w puste pole albo w miejsce poprzedniej
  // podpowiedzi. Nazwy wpisanej ręcznie nie ruszamy: bywa, że w obiegu
  // funkcjonuje nazwa handlowa, a nie ta z REGON.
  useEffect(() => {
    const nazwa = rejestr.wynik?.company?.name ?? "";
    if (!nazwa) return;
    setForm((poprzedni) =>
      poprzedni.counterparty_name.trim() === "" || poprzedni.counterparty_name === zRejestru
        ? { ...poprzedni, counterparty_name: nazwa }
        : poprzedni,
    );
    setZRejestru(nazwa);
  }, [rejestr.wynik, zRejestru]);

  const zapisz = useMutation({
    mutationFn: () =>
      api.post<Commitment>(`/projects/${projectCode}/commitments`, {
        ...form,
        counterparty_nip: form.counterparty_nip.replace(/[^0-9]/g, ""),
        amount_net: parseMoney(form.amount_net) || "0",
      }),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["commitments", projectCode] });
      setForm({ ...form, title: "", counterparty_name: "", counterparty_nip: "",
                amount_net: "" });
      setBlad(null);
      onClose();
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nowe zaangażowanie</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {blad && <Alert severity="error">{blad.message}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Rodzic dla kosztu, który nie wynika z umowy — na przykład zakup
            na planie. Pełny obieg umów dochodzi w kolejnym etapie.
          </Typography>
          <TextField select label="Rodzaj" value={form.kind}
                     onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {RODZAJE.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
          <TextField label="Opis" value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Stack direction="row" spacing={2}>
            <TextField label="Kontrahent" value={form.counterparty_name} sx={{ flex: 2 }}
                       onChange={(e) =>
                         setForm({ ...form, counterparty_name: e.target.value })} />
            <TextField label="NIP (opcjonalnie)" value={form.counterparty_nip} sx={{ flex: 1 }}
                       helperText={komunikatRejestru(rejestr)}
                       error={Boolean(rejestr.bladPola)}
                       onChange={(e) =>
                         setForm({ ...form, counterparty_nip: e.target.value })} />
          </Stack>
          {rejestr.wynik?.warnings.map((uwaga) => (
            <Alert key={uwaga} severity="warning">{uwaga}</Alert>
          ))}
          <TextField label="Kwota netto" value={form.amount_net}
                     onChange={(e) => setForm({ ...form, amount_net: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={() => zapisz.mutate()}
                disabled={zapisz.isPending}>Utwórz</Button>
      </DialogActions>
    </Dialog>
  );
}
